import { Children, FC, ReactEventHandler, useEffect, useState, useRef } from 'react';
import { ICampusItem, ICampusGroup, ICampusTreeGroup, ICampusTreeItem } from '../models/campuses.model';
import { Select, Table, TableColumnsType, SelectProps, Collapse, TreeSelect } from 'antd';
import Checkbox  from './Checkbox';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/rootReducer';
import { setCurrentCampus } from '../store/auth.slice';
import { campusHeadOffice } from '../lib/data';
import { useTranslation } from 'react-i18next';
import { getCampusGroup } from '../lib/util';
import { IStudent } from '../models/campusClasses2.model';

interface IProps {
  hasHq?: boolean;
  disabled?: boolean;
  normalOnly?: boolean;
  hasAll?: boolean;
  className?: string;
  placeholder?: string;
  value: string[];
  onChange?: any;
  setDropdownVisible?: any;
  dropdownVisible?: boolean;
}

const checkTestCampus = (campus: any) => campus.testYn === 'N' && campus.campusCode !== campusHeadOffice;
const checkNoTestCampus = (campus: any) => campus.testYn === 'Y' || campus.campusCode === campusHeadOffice;

const SelectCampusMultiChoice: FC<IProps> = ({
  hasHq = true,
  disabled = false,
  normalOnly = false,
  hasAll = false,
  value,
  className,
  placeholder,
  onChange,
  setDropdownVisible,
  dropdownVisible
}) => {

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { auth, currentCampus } = useSelector((state: RootState) => state.auth);
  const { campusData } = useSelector((state: RootState) => state.campusData);
  const [campusTreeGroups, setCampusTreeGroups] = useState<any>([]);
  const [searchValue, setSearchValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (auth && campusData) {
      let campuses: ICampusItem[] = [];

      if (auth.hqYn ==='Y') {
        campuses = campusData;
      } else {
        auth.campus.forEach(item => {
          const campusItem = campusData.find(subitem => subitem.campusCode === item);
          if (campusItem) campuses.push(campusItem);
        });
      }

      if (!hasHq) {
        campuses = campuses.filter(item => item.campusCode !== campusHeadOffice);
      }

      if (normalOnly) {
        campuses = campuses.filter(item => item.campusStatus === 'NM');
      }

      setCampusTreeGroups(
        [{
          title: '전체',
          value: 'GROUP-1',
          children: [
            {
              title: t('campus.group.text1'),
              value: 'GROUP-1-1',
              children: [...campuses].filter(checkTestCampus).map(item=> {
                return {
                  title: i18n.language === 'ko' ? item.campusKorName : item.campusEngName,
                  value: item.campusCode,
                }
              })
            },
            {
              title: t('campus.group.text2'),
              value: 'GROUP-1-2',
              children: [...campuses].filter(checkNoTestCampus).map(item=> {
                return {
                  key: item.campusCode,
                  title: i18n.language === 'ko' ? item.campusKorName : item.campusEngName,
                  value: item.campusCode,
                }
              })
            }
          ]
        }]
        
      )
    }
  }, [auth, campusData]);

  useEffect(() => {
    if (currentCampus === campusHeadOffice && !hasHq) {
      dispatch(setCurrentCampus(''));
    }
  }, [currentCampus]);

  
  const checkSearchRule = (input: string) => {
    const regex = /^[ㄱ-ㅎ가-힣a-zA-Z0-9 ]{0,20}$/; // 영문, 한글, 숫자를 포함하는 최대 20자
    return regex.test(input);
  }
  
  const handleSearch = (input: string) =>  checkSearchRule(input) && setSearchValue(input);
  const handleDropdownVisibleChange = (isOpen: boolean) => setDropdownOpen(isOpen);
  const filterTreeNode = (input: string, treeNode: any) => treeNode.title.toLowerCase().includes(input.toLowerCase());

  // * Select 시 (해제 시 동작 안함)
  // _onChange에서 사용
  const onSelect = (selectedValue: string) => {
    const updateValue = (value: string[]) => {
      const selectedNode = findNode(campusTreeGroups, selectedValue);
      if (selectedNode && selectedNode.children) {
        const childValues = getLastDepthChildrenValues(selectedNode.children);
        return Array.from(new Set([...childValues]));
      } else {
        // 마지막 뎁스의 항목이 선택된 경우
        return value.includes(selectedValue)
          ? value.filter(v => v !== selectedValue)
          : [...value, selectedValue];
      }
    };
    return updateValue(value).filter(v=> v.indexOf('GROUP-'));
  };

  const findNode = (nodes: any[], value: string): any => {
    for (const node of nodes) {
      if (node.value === value  ) {
        return node;
      }
      if (node.children) {
        const found = findNode(node.children, value);
        if (found) return found;
      }
    }
    return null;
  };

  // Tree Data의 마지막 값 검색
  const getLastDepthChildrenValues = (nodes: any[]): string[] => {
    let values: string[] = [];
    nodes.forEach(node => {
      if (node.children) {
        values = values.concat(getLastDepthChildrenValues(node.children));
      } else {
        values.push(node.value);
      }
    });
    return values;
  };
  
  // Dropdown 목록 체크 시
  const _onChange = (selectedValue: string[]) => {
    const PREFIX_TITLE_GROUP = 'GROUP-';
    onChange([
      ...[selectedValue.filter((v: any)=> v && v.includes(PREFIX_TITLE_GROUP) ).flatMap(onSelect)], 
      ...[selectedValue.filter((v: any)=> v && !v.includes(PREFIX_TITLE_GROUP) )]
    ].flatMap(v=> v));
    
  }

  

  return (
    <TreeSelect
      showSearch
      value={value}
      onChange={_onChange}
      onSearch={handleSearch}
      searchValue={searchValue}
      // onSelect={onSelect}
      filterTreeNode={(input: string, treeNode: any)=> filterTreeNode(searchValue, treeNode)}
      dropdownStyle={{ maxHeight: 1000, overflow: 'auto' }}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      placeholder={ dropdownOpen 
          ? t('common.placeholder.3')
          : (placeholder ?? t('statistics.eLearning.title2'))}
      allowClear
      multiple
      className={className ? className : 'w-210'}
      treeCheckable={true}
      showCheckedStrategy={'SHOW_PARENT'}
      treeData={campusTreeGroups}
      maxTagCount={1}
      treeDefaultExpandAll
    />
  )
}

export default SelectCampusMultiChoice;
